import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import { lazy, Suspense } from "react";
const QuoteModal = lazy(() =>
  import("@/components/QuoteModal").then((m) => ({ default: m.QuoteModal })),
);
import {
  Mail,
  Phone,
  Clock,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground py-12 border-t-2 border-secondary">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Left Column - CONTACT US */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col space-y-6"
          >
            <address className="not-italic flex flex-col space-y-4">
              <h2 className="uppercase tracking-wider text-primary-foreground/70 font-semibold text-sm">
                Contact Us
              </h2>

              <Suspense
                fallback={
                  <Button className="w-full sm:w-fit bg-accent hover:bg-accent/90 text-accent-foreground border-transparent">
                    REQUEST A FREE ESTIMATE!
                  </Button>
                }
              >
                <QuoteModal>
                  <Button className="w-full sm:w-fit bg-accent hover:bg-accent/90 text-accent-foreground border-transparent">
                    REQUEST A FREE ESTIMATE!
                  </Button>
                </QuoteModal>
              </Suspense>

              <div className="flex flex-col gap-3 mt-4 text-primary-foreground/90">
                <a
                  href="mailto:precisionpaver@gmail.com"
                  className="flex items-center gap-3 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  <span>precisionpaver@gmail.com</span>
                </a>
                <a
                  href="tel:+19542003501"
                  className="flex items-center gap-3 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>+1 954-200-3501</span>
                </a>
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 shrink-0 mt-1" />
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold text-primary-foreground/70 uppercase tracking-wider text-xs mb-1">
                      Business Hours
                    </span>
                    <span>Monday - Friday: 8:00 AM - 5:00 PM</span>
                    <span>Saturday: 8:00 AM - 1:00 PM</span>
                    <span>Sunday: Closed</span>
                  </div>
                </div>
              </div>
            </address>
          </motion.div>

          {/* Middle Column - VISIT OUR LOCATION & FOLLOW US */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col space-y-6"
          >
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 group-hover:text-secondary" />
                <h2 className="uppercase tracking-wider text-primary-foreground/70 font-semibold text-sm">
                  Visit Our Location
                </h2>
              </div>
              <a
                href="https://maps.google.com/?q=605+SW+9th+Terrace+Pompano+Beach+FL+33069"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col gap-2 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden w-fit"
              >
                <p className="text-primary-foreground/90 group-hover:text-secondary transition-colors">
                  605 SW 9th Terrace,
                  <br />
                  Pompano Beach, FL 33069
                </p>
              </a>
            </div>

            <Separator className="bg-primary-foreground/10" />

            <div className="flex flex-col space-y-4">
              <h2 className="uppercase tracking-wider text-primary-foreground/70 font-semibold text-sm">
                Follow Us
              </h2>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/precisionpaverspools"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="https://www.instagram.com/precisionpaverspools"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://youtube.com/precisionpavers"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <Youtube className="h-5 w-5" />
                </a>
                <a
                  href="https://www.yelp.com/biz/precision-pavers-and-pool-design-pompano-beach"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Yelp page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden flex items-center justify-center w-5 h-5 border-[1.5px] border-current rounded-full text-[10px] font-bold"
                >
                  Y
                </a>
                <a
                  href="https://tiktok.com/@precisionpavers"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    fill="currentColor"
                    className="h-5 w-5"
                  >
                    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
                  </svg>
                </a>
                <a
                  href="https://www.google.com/maps/place/Pool+Company+-+Precision+Pavers+and+Pool+Design/@26.1974225,-80.0966557,40027m/data=!3m1!1e3!4m6!3m5!1s0x88d91b667550c0a3:0x147439e2ee48aa11!8m2!3d26.2218545!4d-80.1375137!16s%2Fg%2F11jm0q9srh?entry=ttu&g_ep=EgoyMDI2MDUyMC4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Maps page"
                  className="text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                >
                  <MapPin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right Column - SITE NAVIGATION */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col space-y-6"
          >
            <nav
              aria-label="Site navigation"
              className="flex flex-col space-y-4"
            >
              <h2 className="uppercase tracking-wider text-primary-foreground/70 font-semibold text-sm">
                Site Navigation
              </h2>
              <ul role="list" className="flex flex-col gap-2">
                <li>
                  <Link
                    to="/"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    HOME
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    ABOUT US
                  </Link>
                </li>
                <li>
                  <Link
                    to="/projects"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    PROJECTS
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    SERVICES
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    AREAS
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    BLOG
                  </Link>
                </li>

                <li>
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
                  >
                    CONTACT
                  </Link>
                </li>
                <li>
                  <Suspense
                    fallback={
                      <button className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden text-left">
                        FREE QUOTE
                      </button>
                    }
                  >
                    <QuoteModal>
                      <button className="block w-fit text-primary-foreground/90 hover:text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden text-left">
                        FREE QUOTE
                      </button>
                    </QuoteModal>
                  </Suspense>
                </li>
              </ul>
            </nav>
          </motion.div>
        </motion.div>

        <Separator className="bg-primary-foreground/10 mt-12 mb-6" />

        <div className="flex flex-col items-center justify-center gap-2 text-center text-sm text-primary-foreground/50">
          <p>
            Copyright &copy; {new Date().getFullYear()} Precision Pavers & Pool
            Design | All Rights Reserved |{" "}
            <Link
              to="/privacy-policy"
              onClick={() => window.scrollTo(0, 0)}
              className="text-primary-foreground/90 hover:text-secondary transition-colors font-medium"
            >
              Privacy
            </Link>{" "}
            |{" "}
            <Link
              to="/terms-conditions"
              onClick={() => window.scrollTo(0, 0)}
              className="text-primary-foreground/90 hover:text-secondary transition-colors font-medium"
            >
              Terms & Conditions
            </Link>
          </p>
          <p>
            Website designed &amp; powered by{" "}
            <a
              href="https://www.boostlocalbiz.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-foreground/90 hover:text-secondary transition-colors font-medium"
            >
              Boost Local Biz
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
